import type { components } from '@connected/api-client/api.generated';

import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';

import type { DistributionBoard } from '@/hooks/use-distribution-board';

import { DistributionBoardContext } from '@/hooks/use-distribution-board';
import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';

export function DistributionBoardProvider({ children }: { children: React.ReactNode }) {
    const params = useParams({ from: '/(auth)/projects/$projectVersionId/re-inspections/distributionboards/$distributionboardId' });

    const distributionBoardListOptions = useMemo(() => elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardListOptions({ electricalProjectVersionId: params.projectVersionId }), [params.projectVersionId]);
    // TODO: Refactor this to use the distribution board list query. Currently, this is not possible because the distribution board options query is used throughout the app.
    const distributionBoardOptions = useMemo(() => elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({ electricalProjectDistributionBoardId: params.distributionboardId }), [params.distributionboardId]);

    const distributionBoardList = useSuspenseQuery(distributionBoardListOptions);

    const distributionBoard = useMemo(() => distributionBoardList.data?.DistributionBoards.find(db => db.ElectricalProjectDistributionBoardId === params.distributionboardId) || null, [distributionBoardList.data, params.distributionboardId]);

    const optimisticUpdate = useCallback((updatedDistributionBoard: Partial<DistributionBoard>) => {
        if (!params.distributionboardId)
            return undefined;

        const previousDistributionBoardListData = queryClient.getQueryData(distributionBoardListOptions.queryKey) as components['schemas']['ListElectricalProjectDistributionBoardQueryResult'];
        const previousDistributionBoardData = queryClient.getQueryData(distributionBoardOptions.queryKey) as components['schemas']['EditElectricalProjectDistributionBoardQueryResult'];

        if (!previousDistributionBoardListData || !previousDistributionBoardData) {
            logger.warn({
                message: 'optimisticUpdate(distributionBoardList)',
                description: 'Distribution board list not found',
                distributionBoardList: {
                    queryKey: distributionBoardListOptions.queryKey,
                    distributionBoard: {
                        queryKey: distributionBoardOptions.queryKey,
                    },
                },
            });

            return;
        }

        const newDistributionBoardListRow: DistributionBoard = {
            ...previousDistributionBoardListData.DistributionBoards.find(db => db.ElectricalProjectDistributionBoardId === params.distributionboardId),
            ...updatedDistributionBoard,
        };

        const newDistributionBoardListData: components['schemas']['ListElectricalProjectDistributionBoardQueryResult'] = {
            ...previousDistributionBoardListData,
            DistributionBoards: [
                ...previousDistributionBoardListData.DistributionBoards.filter(db => db.ElectricalProjectDistributionBoardId !== params.distributionboardId),
                newDistributionBoardListRow,
            ],
        };

        // TODO: Refactor this to use the distribution board list query. Currently, this is not possible because the distribution board options query is used throughout the app.
        const newDistributionBoardData: components['schemas']['EditElectricalProjectDistributionBoardQueryResult'] = {
            ...previousDistributionBoardData,
            DistributionBoard: {
                ...previousDistributionBoardData.DistributionBoard,
                ...updatedDistributionBoard,
                ElectricalProjectDistributionBoardId: params.distributionboardId,
            },
        };

        queryClient.setQueryData(distributionBoardListOptions.queryKey, newDistributionBoardListData);
        queryClient.setQueryData(distributionBoardOptions.queryKey, newDistributionBoardData);

        logger.debug({
            message: 'optimisticUpdate(distributionBoardList)',
            distributionBoardList: {
                previousData: previousDistributionBoardListData,
                newData: newDistributionBoardListData,
                queryKey: distributionBoardListOptions.queryKey,
                updatedDistributionBoard,
            },
        });

        return {
            queryOptions: distributionBoardListOptions,
            previousData: previousDistributionBoardListData,
        };
    }, [params.distributionboardId, distributionBoardListOptions, distributionBoardOptions.queryKey]);

    const context = useMemo<DistributionBoardContext>(() => ({ distributionBoards: distributionBoardList.data?.DistributionBoards || [], distributionBoard, optimisticUpdate }), [distributionBoardList.data?.DistributionBoards, distributionBoard, optimisticUpdate]);

    return <DistributionBoardContext value={context}>{children}</DistributionBoardContext>;
}
